import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  ChevronDown,
  X,
  Shield,
  Brain,
  Target,
  Megaphone,
  Users,
  Globe,
  Building2,
  Rocket,
  Heart,
  HeartPulse,
  HelpCircle,
  Search,
  Info,
  Mail,
} from "lucide-react";
import Logo from "@/components/Logo";
import { PillButton } from "./PillButton";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

type Item = { label: string; to: string; desc?: string; icon?: typeof Shield };
type NavGroup = { label: string; items: Item[]; layout?: "grid" | "list" };

const groups: NavGroup[] = [
  {
    label: "Product",
    layout: "grid",
    items: [
      { label: "Anonymous Feedback", to: "/how-anonymity-works", desc: "True zero-knowledge architecture", icon: Shield },
      { label: "AI Insights", to: "/product/ai-insights", desc: "Sentiment, themes & risk alerts", icon: Brain },
      { label: "HR Action Plans", to: "/product/hr-action-plans", desc: "Close the loop on every signal", icon: Target },
      { label: "Pulse Campaigns", to: "/product/pulse-campaigns", desc: "Continuous, lightweight check-ins", icon: Megaphone },
    ],
  },
  {
    label: "Use Cases",
    layout: "grid",
    items: [
      { label: "HR Teams", to: "/solutions/hr-teams", desc: "People ops & culture leads", icon: Users },
      { label: "Remote Teams", to: "/solutions/remote-teams", desc: "Async, distributed workforces", icon: Globe },
      { label: "Enterprise", to: "/solutions/enterprise", desc: "Scale with security & SSO", icon: Building2 },
      { label: "Startups", to: "/solutions/startups", desc: "Move fast, listen faster", icon: Rocket },
      { label: "Culture Building", to: "/solutions/culture-building", desc: "Make values measurable", icon: Heart },
      { label: "Employee Retention", to: "/solutions/employee-retention", desc: "Spot churn risk early", icon: HeartPulse },
    ],
  },
  {
    label: "Resources",
    layout: "list",
    items: [
      { label: "Help Center", to: "/help", desc: "Docs & answers", icon: HelpCircle },
      { label: "Track Feedback", to: "/track", desc: "Anonymous submission tracker", icon: Search },
      { label: "About", to: "/about", desc: "Mission & team", icon: Info },
      { label: "Contact", to: "/contact", desc: "Talk to us", icon: Mail },
    ],
  },
];

export const MarketingNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpen(null);
  }, [location.pathname]);

  const isActiveGroup = (g: NavGroup) =>
    g.items.some((i) => location.pathname.startsWith(i.to));
  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 px-3 sm:px-4 pt-3">
      <div
        className={cn(
          "mx-auto max-w-6xl rounded-full border transition-all duration-300",
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-border/70 shadow-[0_10px_40px_-15px_hsl(var(--foreground)/0.18)]"
            : "bg-white/70 backdrop-blur-md border-border/50 shadow-[0_4px_24px_-12px_hsl(var(--foreground)/0.08)]",
        )}
      >
        <div className="h-14 lg:h-16 px-3 lg:px-4 flex items-center justify-between gap-2">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0 pl-1 lg:pl-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            aria-label="Home"
          >
            <Logo size="md" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {groups.map((g) => {
              const active = isActiveGroup(g);
              const isOpen = open === g.label;
              return (
                <div
                  key={g.label}
                  className="relative"
                  onMouseEnter={() => setOpen(g.label)}
                  onMouseLeave={() => setOpen(null)}
                >
                  <button
                    className={cn(
                      "h-10 px-4 inline-flex items-center gap-1 text-[14px] font-medium rounded-full transition-colors",
                      active || isOpen
                        ? "text-foreground bg-foreground/[0.06]"
                        : "text-foreground/70 hover:text-foreground hover:bg-foreground/[0.04]",
                    )}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                  >
                    {g.label}
                    <ChevronDown
                      className={cn(
                        "h-3.5 w-3.5 opacity-60 transition-transform duration-200",
                        isOpen && "rotate-180",
                      )}
                    />
                  </button>

                  {isOpen && (
                    <div
                      className={cn(
                        "absolute top-full pt-3 animate-in fade-in slide-in-from-top-1 duration-150",
                        g.layout === "grid"
                          ? "left-1/2 -translate-x-1/2 w-[640px]"
                          : "left-1/2 -translate-x-1/2 w-[340px]",
                      )}
                    >
                      <div className="bg-card rounded-3xl border border-border/70 shadow-[0_24px_60px_-20px_hsl(var(--foreground)/0.25)] p-3">
                        <div
                          className={cn(
                            g.layout === "grid"
                              ? "grid grid-cols-2 gap-1"
                              : "flex flex-col",
                          )}
                        >
                          {g.items.map((it) => {
                            const Icon = it.icon;
                            const itemActive = isActive(it.to);
                            return (
                              <Link
                                key={it.to + it.label}
                                to={it.to}
                                className={cn(
                                  "group flex items-start gap-3 px-3 py-3 rounded-2xl transition",
                                  itemActive
                                    ? "bg-[hsl(var(--mkt-primary-soft))]/60"
                                    : "hover:bg-muted/70",
                                )}
                              >
                                {Icon && (
                                  <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--mkt-primary-soft))] text-primary shrink-0 group-hover:scale-105 transition-transform">
                                    <Icon className="h-4.5 w-4.5" />
                                  </span>
                                )}
                                <span className="min-w-0 flex-1">
                                  <span className="block text-[14px] font-semibold text-foreground leading-tight">
                                    {it.label}
                                  </span>
                                  {it.desc && (
                                    <span className="block text-[12.5px] text-muted-foreground mt-0.5 leading-snug">
                                      {it.desc}
                                    </span>
                                  )}
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <Link
              to="/pricing"
              className={cn(
                "h-10 px-4 inline-flex items-center text-[14px] font-medium rounded-full transition-colors",
                isActive("/pricing")
                  ? "text-foreground bg-foreground/[0.06]"
                  : "text-foreground/70 hover:text-foreground hover:bg-foreground/[0.04]",
              )}
            >
              Pricing
            </Link>
          </nav>

          {/* Right CTAs */}
          <div className="hidden lg:flex items-center gap-1.5 pr-1">
            {user ? (
              <PillButton variant="primary" size="md" onClick={() => navigate("/dashboard")}>
                Dashboard
              </PillButton>
            ) : (
              <>
                <Link
                  to="/auth"
                  className="text-[14px] font-medium px-3 h-10 inline-flex items-center text-foreground/75 hover:text-foreground rounded-full hover:bg-foreground/[0.04] transition"
                >
                  Login
                </Link>
                <PillButton variant="primary" size="md" onClick={() => navigate("/auth?mode=signup")}>
                  Start free
                </PillButton>
              </>
            )}
          </div>

          {/* Mobile */}
          <div className="lg:hidden flex items-center gap-1.5">
            {!user && (
              <PillButton variant="primary" size="sm" onClick={() => navigate("/auth?mode=signup")}>
                Start free
              </PillButton>
            )}
            <button
              className="h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-foreground/[0.05] transition"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden mt-2 mx-auto max-w-6xl rounded-3xl bg-card border border-border/70 shadow-[0_24px_60px_-20px_hsl(var(--foreground)/0.25)] animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-4 space-y-5 max-h-[calc(100vh-120px)] overflow-y-auto">
            {groups.map((g) => (
              <div key={g.label}>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground px-2 mb-1.5">
                  {g.label}
                </div>
                <div className="flex flex-col">
                  {g.items.map((it) => {
                    const Icon = it.icon;
                    return (
                      <Link
                        key={it.to + it.label}
                        to={it.to}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-2 py-2.5 rounded-2xl hover:bg-muted/70 transition"
                      >
                        {Icon && (
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--mkt-primary-soft))] text-primary shrink-0">
                            <Icon className="h-4.5 w-4.5" />
                          </span>
                        )}
                        <span className="text-[15px] font-semibold text-foreground">{it.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
            <Link
              to="/pricing"
              onClick={() => setMobileOpen(false)}
              className="block px-2 py-2.5 text-[15px] font-semibold text-foreground rounded-2xl hover:bg-muted/70"
            >
              Pricing
            </Link>
            <div className="pt-3 border-t border-border flex flex-col gap-2">
              {user ? (
                <PillButton variant="primary" size="lg" onClick={() => { navigate("/dashboard"); setMobileOpen(false); }}>
                  Open Dashboard
                </PillButton>
              ) : (
                <>
                  <PillButton variant="outline" size="lg" onClick={() => { navigate("/auth"); setMobileOpen(false); }}>
                    Login
                  </PillButton>
                  <PillButton variant="primary" size="lg" onClick={() => { navigate("/auth?mode=signup"); setMobileOpen(false); }}>
                    Start free
                  </PillButton>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
